import {
  DocumentTextIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  TableIcon,
  VariableIcon,
} from "@heroicons/react/outline";
import { FilesIcon, WrenchIcon } from "lucide-react";

import { AbReleases, Agent, Dataset, Evaluation, Prompt } from ".";

import { ENTITY_COLORS } from "@/components/UnifiedRegistry/utils";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries/folder-entities/types";

const getSizeClass = (size: "xs" | "sm" | "md" | "lg"): string => {
  switch (size) {
    case "xs":
      return "h-3 w-3";
    case "sm":
      return "h-4 w-4";
    case "md":
      return "h-5 w-5";
    case "lg":
      return "h-6 w-6";
    default:
      return "h-4 w-4";
  }
};

interface EntityIconProps {
  type: EntityType;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const EntityIcon = ({ type, disabled = false, size = "sm", className }: EntityIconProps) => {
  const colorClass = disabled ? "text-gray-400" : ENTITY_COLORS[type]?.text || "text-gray-500";
  const sizeClass = getSizeClass(size);

  switch (type) {
    case EntityType.PROMPT:
      return <Prompt className={cn(sizeClass, colorClass, className)} />;
    case EntityType.WORKFLOW:
      return <Agent className={cn(sizeClass, colorClass, className)} />;
    case EntityType.FOLDER:
      return <FolderIcon className={cn(sizeClass, colorClass, className)} />;
    case EntityType.DATASET:
      return <Dataset className={cn(sizeClass, colorClass, className)} />;
    case EntityType.REPORT:
      return <Evaluation className={cn(sizeClass, colorClass, className)} />;
    case EntityType.AB_TEST:
      return <AbReleases className={cn(sizeClass, colorClass, className)} />;
    case EntityType.SNIPPET:
      return <DocumentTextIcon className={cn(sizeClass, colorClass, className)} />;
    case EntityType.INPUT_VARIABLE_SET:
      return <VariableIcon className={cn(sizeClass, colorClass, className)} />;
    case EntityType.SMART_TABLE:
      return <TableIcon className={cn(sizeClass, colorClass, className)} />;
    case EntityType.SKILL_COLLECTION:
      return <FilesIcon className={cn(sizeClass, colorClass, className)} />;
    case EntityType.TOOL:
      return <WrenchIcon className={cn(sizeClass, colorClass, className)} />;
    default:
      // Fallback icon for unknown entity types
      return <QuestionMarkCircleIcon className={cn(sizeClass, "text-gray-400", className)} />;
  }
};

export default EntityIcon;
